import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, KeyRound, LoaderCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/site/PageShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 128;

function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const message = detail.find((item) => item?.msg)?.msg;
    if (message) return message;
  }

  return "Das Passwort konnte nicht geändert werden. Der Link ist möglicherweise abgelaufen oder wurde bereits verwendet.";
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();

    if (loading || success) return;

    if (!token) {
      setError("Der Reset-Link ist unvollständig. Bitte fordere in BeParent einen neuen Link an.");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }

    if (password.length > MAX_PASSWORD_LENGTH) {
      setError(`Das Passwort darf höchstens ${MAX_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/password-reset/confirm`, {
        token,
        password,
      });
      setPassword("");
      setPasswordConfirmation("");
      setSuccess(true);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  const missingToken = !token;

  return (
    <PageShell
      testId="reset-password-page"
      eyebrow="Konto"
      title={
        <>
          Passwort zurücksetzen.{" "}
          <span className="text-white/40">Sicher zurück zu BeParent.</span>
        </>
      }
      subtitle="Vergib ein neues Passwort für dein BeParent-Konto."
    >
      <div className="mx-auto max-w-xl">
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#152036] via-[#0F172A] to-[#0B1120] p-6 shadow-2xl shadow-black/20 sm:p-8 md:p-10">
          <div
            aria-hidden
            className="absolute -right-32 -top-32 h-80 w-80 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(0,250,248,0.14), transparent 60%)",
              filter: "blur(24px)",
            }}
          />
          <div aria-hidden className="absolute inset-0 bp-grid opacity-30" />

          <div className="relative">
            {success ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="reset-password-success"
                className="py-5 text-center"
                role="status"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-[#00FAF8]/40 bg-[#00FAF8]/10">
                  <Check size={26} className="text-[#00FAF8]" />
                </div>
                <h2 className="font-display text-2xl font-medium text-white sm:text-3xl">
                  Passwort geändert
                </h2>
                <p className="mt-4 leading-relaxed text-white/60">
                  Dein Passwort wurde erfolgreich geändert. Du kannst dich jetzt in BeParent mit deinem neuen Passwort anmelden.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="mb-7 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#00FAF8]/30 bg-[#00FAF8]/10">
                    <KeyRound size={18} className="text-[#00FAF8]" />
                  </div>
                  <div>
                    <h2 className="font-display text-xl font-medium text-white">
                      Neues Passwort
                    </h2>
                    <p className="mt-1 text-sm text-white/50">
                      Mindestens {MIN_PASSWORD_LENGTH} Zeichen
                    </p>
                  </div>
                </div>

                {missingToken && (
                  <div className="mb-6 rounded-2xl border border-amber-300/25 bg-amber-300/10 px-4 py-3 text-sm leading-relaxed text-amber-100" role="alert">
                    Der Reset-Link ist unvollständig. Bitte fordere in BeParent einen neuen Link an.
                  </div>
                )}

                <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
                  <label className="block">
                    <span className="mb-2 block text-sm text-white/70">Neues Passwort</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      required
                      disabled={missingToken || loading}
                      data-testid="reset-password-input"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-base text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#00FAF8]/60 focus:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm text-white/70">Passwort bestätigen</span>
                    <input
                      type="password"
                      value={passwordConfirmation}
                      onChange={(event) => setPasswordConfirmation(event.target.value)}
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      required
                      disabled={missingToken || loading}
                      data-testid="reset-password-confirmation-input"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-base text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#00FAF8]/60 focus:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </label>

                  {error && (
                    <div className="rounded-2xl border border-red-300/25 bg-red-300/10 px-4 py-3 text-sm leading-relaxed text-red-100" role="alert" data-testid="reset-password-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={missingToken || loading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#00FAF8] px-6 py-4 font-medium text-[#0F172A] transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <LoaderCircle size={18} className="animate-spin" />
                        Passwort wird geändert …
                      </>
                    ) : (
                      "Passwort ändern"
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
